import { TextField } from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FormAutocomplete,
  formClasses,
  labelClasses,
  LabeledAutocomplete,
} from "../styles/AutocompleteStyles";
import useUrlParams from "../customHooks/useUrlParams";
import {
  GetActiveFinYr,
  GetFinYrs,
} from "../components/modules/customer/financialYear/queries/queries";
import {
  AtsFinYr,
  GetActiveFinYrResult,
  GetFinYrsResult,
  GetFinYrsVars,
} from "../components/modules/customer/financialYear/queries/types";
import { useLazyQuery } from "@apollo/client";
import { AppContext } from "../context/context";
import { payloadTypes } from "../context/reducer";
interface Props {
  value: number;
  onChange: (newValue: AtsFinYr | null) => void;
  label: string;
  disabled?: boolean;
}
const FinYrAutocomplete = ({ value, onChange, label, disabled }: Props) => {
  const [searchData, setSearchData] = useState("");

  const [autoValue, setAutoValue] = useState<AtsFinYr | null>(null);

  const { state, dispatch } = useContext(AppContext);

  const { customer_id, company_id } = useUrlParams();

  const [GetFinancialYears, { data }] = useLazyQuery<
    GetFinYrsResult,
    GetFinYrsVars
  >(GetFinYrs, { fetchPolicy: "network-only" });

  const [GetActiveFin, { data: activeFinYrData }] = useLazyQuery<
    GetActiveFinYrResult,
    GetFinYrsVars
  >(GetActiveFinYr, { fetchPolicy: "network-only" });

  const financialYearList = useMemo(() => {
    return data ? data.GetFinYrs : [];
  }, [data]);

  const commonAutocompleteProps = {
    className: label ? labelClasses.inputRoot : formClasses.inputRoot,
    options: financialYearList,
    openOnFocus: true,
    disabled: disabled,
    onChange: (e: React.SyntheticEvent<Element, Event>, value: unknown) =>
      onChange(value as AtsFinYr | null),
  };

  const commonTextFieldProps = {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setSearchData(e.target.value),
    label: label || "",
    fullWidth: true,
    InputLabelProps: { shrink: label.length > 0 },
    className: label
      ? labelClasses.formControlRoot
      : formClasses.formControlRoot,
  };

  const Autocomplete =
    label.length > 0 ? LabeledAutocomplete : FormAutocomplete;

  useEffect(() => {
    if (value) {
      const foundValue = financialYearList.find(
        ({ id: optionsValue }) => Number(optionsValue) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    }
  }, [value, data, financialYearList]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (customer_id || company_id) {
          GetFinancialYears({
            variables: {
              customerID: customer_id ? customer_id : 0,
              companyID: company_id ? company_id : 0,
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [customer_id, GetFinancialYears]);

  useEffect(() => {
    const fetchData = async () => {
      if (state.ActiveFinYr === null && customer_id) {
        GetActiveFin({
          variables: {
            customerID: customer_id,
            companyID: 0,
          },
        }).then(({ data }) => {
          if (data && data.GetActiveFinYr) {
            dispatch({
              type: payloadTypes.SET_ACTIVE_FIN_YR,
              payload: {
                ActiveFinYr: {
                  ...data.GetActiveFinYr,
                },
              },
            });
          }
        });
      }
    };

    fetchData();
  }, [state.ActiveFinYr, customer_id, GetActiveFin, activeFinYrData]);

  return (
    <Autocomplete
      {...commonAutocompleteProps}
      getOptionLabel={(option) => (option as AtsFinYr).finYr}
      value={autoValue}
      isOptionEqualToValue={(option) =>
        autoValue ? (option as AtsFinYr).id === autoValue.id : false
      }
      renderInput={(params) => (
        <TextField {...params} {...commonTextFieldProps} />
      )}
    />
  );
};

export default FinYrAutocomplete;
